"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { ContactPanel } from "@/components/contact-panel";
import { AlertPreview } from "@/components/alert-preview";
import { SheriffLogo } from "@/components/sheriff-logo";

/**
 * Hero — the five-second pitch. A first-time visitor must leave this screen
 * knowing: WHAT (AI-powered endpoint security), WHO (SMEs and cyber cafés in
 * Kenya / East Africa), WHY (enterprise-grade protection at local prices).
 * One dominant CTA (Waiting List), one visually subordinate secondary (Pricing).
 * Nav uses concrete labels only. Right column shows a real product moment —
 * a plain-English alert with its Approve / Dismiss decision.
 */

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#pricing", label: "Pricing" },
  { href: "#download", label: "Waiting List" },
  { href: "#about", label: "About" },
];

export function Hero() {
  // Contact opens a slide-in panel — a button, not a link, so clicking it
  // never navigates or reloads.
  const [contactOpen, setContactOpen] = useState(false);

  // Exposes the same action to an in-browser agent via WebMCP. Registered
  // here (not globally in layout.tsx) because ContactPanel only exists on
  // whichever page renders Hero — the tool should only be advertised where
  // the action it performs actually exists.
  useEffect(() => {
    const modelContext =
      (navigator as any).modelContext || (document as any).modelContext;
    if (!modelContext?.registerTool) return;
    const controller = new AbortController();
    modelContext.registerTool(
      {
        name: "open_contact_panel",
        title: "Open contact panel",
        description:
          "Open the contact panel showing how to reach Pisoque Technologies (WhatsApp, phone, SMS).",
        inputSchema: { type: "object", properties: {} },
        execute: async () => {
          setContactOpen(true);
          return { ok: true };
        },
      },
      { signal: controller.signal },
    );
    return () => controller.abort();
  }, []);

  return (
    <header id="top" className="relative mx-auto w-full max-w-6xl px-6 pt-6">
      {/* Top bar */}
      <div className="relative flex items-center justify-between gap-4">
        <a
          href="#top"
          className="flex min-h-[44px] items-center gap-3"
          aria-label="SHERIFF-AI EDR home"
        >
          <SheriffLogo size={46} uid="nav" />
          <span className="flex flex-col leading-tight">
            <span className="text-lg font-extrabold tracking-tight text-brand">
              SHERIFF-AI EDR
            </span>
            <span className="mono-label text-muted">by Pisoque Technologies</span>
          </span>
        </a>

        <nav aria-label="Main" className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="inline-flex min-h-[44px] items-center rounded-lg px-4 text-base font-medium text-text transition-colors hover:text-accent-text"
            >
              {l.label}
            </a>
          ))}
          <button
            type="button"
            onClick={() => setContactOpen(true)}
            className="inline-flex min-h-[44px] items-center rounded-lg px-4 text-base font-medium text-text transition-colors hover:text-accent-text"
          >
            Contact
          </button>
        </nav>

        <ThemeToggle />
      </div>

      {/* Mobile nav — same concrete labels, visible without a hamburger */}
      <nav
        aria-label="Main mobile"
        className="relative mt-3 flex flex-wrap gap-1 md:hidden"
      >
        {NAV_LINKS.map((l) => (
          <a
            key={l.href}
            href={l.href}
            className="glass inline-flex min-h-[44px] items-center rounded-lg px-4 text-base font-medium text-text"
          >
            {l.label}
          </a>
        ))}
        <button
          type="button"
          onClick={() => setContactOpen(true)}
          className="glass inline-flex min-h-[44px] items-center rounded-lg px-4 text-base font-medium text-text"
        >
          Contact
        </button>
      </nav>

      <ContactPanel open={contactOpen} onClose={() => setContactOpen(false)} />

      <div className="grid items-center gap-10 py-12 md:grid-cols-[1.1fr_0.9fr] md:py-20">
        {/* Copy */}
        <div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-[color-mix(in_srgb,var(--surface-solid)_70%,transparent)] px-4 py-2 text-sm font-medium text-muted"
          >
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
            AI-powered endpoint detection &amp; response
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut", delay: 0.05 }}
            className="mt-5 text-balance text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl"
          >
            Enterprise-grade security.
            <br />
            <span className="text-accent-text">Cyber-café prices.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-muted"
          >
            SHERIFF-AI EDR protects every computer in your business — built for SMEs
            and cyber cafés across Kenya and East Africa. It explains each threat
            in plain English and never acts without your approval. From{" "}
            <strong className="font-semibold text-text">
              KES 200 per device per month.
            </strong>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Button href="#download">
              Join Waiting List →
            </Button>
            <Button href="#pricing" variant="ghost">
              See Pricing
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 text-sm text-muted"
          >
            Linux and Windows, both fully supported · installs in one command
          </motion.p>
        </div>

        {/* A real product moment: plain-English alert + human decision */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mx-auto hidden w-full max-w-sm sm:block"
        >
          <AlertPreview />
        </motion.div>
      </div>
    </header>
  );
}
